/**
 * CONSULT-NEXT-GUARD-V1 -- the consult-page NEXT-band label ladder (a minimal,
 * truthful state guard; NOT the full NEXT derivation).
 *
 * WALK FINDING W-23 (2026-08-08): the consult NEXT band read "Close the visit
 * when the consult ends ..." on a visit that was NOT STARTED and whose required
 * clinical intake was unreviewed -- the software pointed the doctor at the LAST
 * step before the FIRST step was possible, two contradictory instructions on
 * one screen. FOUNDER RULING (2026-08-08): NEXT must describe the next
 * EXECUTABLE action for the visit's CURRENT state.
 *
 * The general NEXT-IS-EXECUTABLE law is reserved for a separate lead-surface
 * package (founder order) -- this module cites the ruling TEXT only, never a
 * repo law that does not exist yet. Until the Cockpit wiring package (Doctor B1)
 * owns full NEXT derivation, this installs a minimal truthful guard, NOT the
 * full derivation: it changes only WHAT the band SAYS per state, never what
 * actions the page offers.
 *
 * DERIVED FROM EXISTING VIEW-MODELS ONLY -- no new query, no new API call. The
 * two inputs are already on the consult page: the doctor case-sheet view and
 * the mirrored preconsult intake panel state. `sheet === null` as the
 * not-started signal, and `rollupPlan().advised` as the open-advised-lines
 * count, are the SAME reads buildJourney / suggestNextActions already use
 * (frontend/src/dashboard/clinical-workbench/case-sheet-cockpit-view.ts).
 *
 * THE LADDER (earliest applicable rung wins; rung 2 re-keyed by
 * DOCTOR-INTAKE-ONE-FLOW-V1 -- the review ceremony is retired, ANSWERED is
 * the truth; rung 0 added by Lane 1 item 1, the D01 corrective):
 *   0. the appointment's stored status admits no consult at all
 *      (cancelled / no-show / not yet arrived)         -> the STATE, not an
 *                                                         instruction
 *   1. visit not started (no case sheet materialized) -> Start the consultation
 *   2. started, required intake not yet ANSWERED       -> Complete the
 *                                                          clinical intake
 *   3. intake complete, open advised treatment lines   -> Record treatment
 *                                                          decisions
 *   4. intake complete, decisions recorded (no open    -> Close the visit
 *      advised lines)                                      (copy UNCHANGED)
 *
 * The band is ADVISORY (guidance text; the Close act is a separate strip below),
 * so a conservative default never traps the doctor: whenever the intake read is
 * not yet KNOWN complete (loading / error / required-unanswered) the ladder
 * holds at rung 2 rather than advance to Close -- the exact W-23 ratchet (NEXT
 * never says "Close the visit" while not-started or required-intake-unanswered).
 */

import type { CaseSheetClinicalView } from '@/dashboard/api';
import type { PanelState as PreconsultPanelState } from '@/dashboard/doctor/PreconsultIntakePanel';
import { rollupPlan } from '@/dashboard/clinical-workbench/case-sheet-cockpit-view';
import { CONSULT_OPEN_NEXT_ACTION_LABEL } from '@/dashboard/appointments/workflow-handoff';
import {
  consultDoorVerdict,
  type ConsultDoorVerdict,
} from '@/dashboard/doctor/consult-eligibility';

/** Rung 1 -- no case sheet exists yet (the "not started" state; the same
 *  `sheet === null` signal buildJourney / suggestNextActions already read). */
export const CONSULT_NEXT_START_LABEL = 'Start the consultation';
export const CONSULT_NEXT_LOADING_INTAKE_LABEL = 'Loading clinical intake';
export const CONSULT_NEXT_UNAVAILABLE_INTAKE_LABEL = 'Clinical intake unavailable';

/** Rung 2 -- started, the required clinical intake is not yet ANSWERED.
 *  DOCTOR-INTAKE-ONE-FLOW-V1 (R1/R3c, founder ruling 2026-08-14): the
 *  review ceremony is retired, so the rung keys on ANSWERED-completeness
 *  (the served `completion.complete`, derived server-side over the org's
 *  required set -- which the block now renders IN FULL, reason included:
 *  GATE SET == VISIBLE SET, LES-150) and its label names the executable
 *  act. The old constant name/copy ("Complete and review the clinical
 *  intake") died with the ceremony. */
export const CONSULT_NEXT_COMPLETE_INTAKE_LABEL =
  'Complete the clinical intake';

/** Rung 3 -- intake cleared, open advised treatment lines await a decision. */
export const CONSULT_NEXT_RECORD_DECISIONS_LABEL = 'Record treatment decisions';

/** Rung 4 reuses the EXISTING close copy verbatim (the shared handoff line) --
 *  this package changes WHEN it is shown, never the string itself. */
export const CONSULT_NEXT_CLOSE_LABEL = CONSULT_OPEN_NEXT_ACTION_LABEL;

export interface ConsultNextActionInput {
  /** The doctor case-sheet view already on the page (null = not started). */
  readonly sheet: CaseSheetClinicalView | null;
  /** The APPOINTMENT'S STORED STATUS, already on the page (the consult
   *  context's `appointment.status`) -- rung 0's input, read through THE
   *  SHARED eligibility rule, never compared inline (Lane 1 item 1 / D01).
   *
   *  OMITTED = the caller has no status yet (still loading), and the ladder
   *  behaves exactly as it did before this rung existed: no status, no rung 0,
   *  no invented state. */
  readonly appointmentStatus?: string | null;
  /** The mirrored preconsult intake panel state already on the page. */
  readonly intake: PreconsultPanelState;
  /** DOCTOR-INTAKE-HISTORY-V1: LIVE answered-completeness from the intake
   *  block's own state -- TRUE only when the page proves every REQUIRED
   *  question in the served set is answered in the block's live answer
   *  state (consultLiveRequiredAnswered below). Clears rung 2 the moment
   *  the acts happen (the walked in-session lag: the mounted read cannot
   *  refresh itself); absent/false = the mounted-read truth alone (the
   *  conservative W-23 default, unchanged). */
  readonly liveRequiredAnswered?: boolean;
}

/**
 * DOCTOR-INTAKE-HISTORY-V1 -- the live-completeness derivation the page
 * feeds the rung: every REQUIRED question key in the SERVED set is present
 * in the block's live answered-key set. The SAME grain the server's
 * completion derivation demands (required over the org set) and the SAME
 * source the Go door already tracks (the block's lifted answeredKeys), so
 * the rung can never clear on less than the gate's own requirement. Pure +
 * exported (pinned).
 */
export function consultLiveRequiredAnswered(
  questions: ReadonlyArray<{ readonly key: string; readonly required: boolean }>,
  answeredKeys: ReadonlySet<string>,
): boolean {
  const required = questions.filter((q) => q.required);
  if (required.length === 0) return false;
  return required.every((q) => answeredKeys.has(q.key));
}

/**
 * TRUE when the intake no longer holds the ladder at rung 2: every REQUIRED
 * question in the org set has a PERSISTED answer (the served
 * `completion.complete` -- the SAME canonical derivation the retired #506
 * gate used, now advisory-only), or there is no intake surface at all. A
 * loading / error / incomplete read is NOT cleared -- the conservative W-23
 * default keeps the band off "Close the visit" until the intake is known
 * complete. (DOCTOR-INTAKE-ONE-FLOW-V1: the old predicate keyed on the
 * reviewed stamp; with the ceremony retired, ANSWERED is the truth. An
 * unanswered-but-required intake now holds rung 2 even when nothing was
 * patient-reported -- asking the questions is the doctor's own first act.)
 */
function intakeCleared(
  intake: PreconsultPanelState,
  liveRequiredAnswered?: boolean,
): boolean {
  // DOCTOR-INTAKE-HISTORY-V1: proven-live completeness clears the rung
  // in-session (the demand line must not outlive the act it demands).
  if (liveRequiredAnswered === true) return true;
  if (intake.kind === 'unavailable') return true;
  if (intake.kind === 'ready') return intake.view.completion.complete;
  // loading | error -> not known cleared (hold at rung 2).
  return false;
}

/**
 * Rung 0's reason, or null when the appointment admits a consult (or when the
 * caller served no status yet). Pure + exported so the rung is a PINNED unit
 * of its own, testable without the rest of the ladder.
 *
 * ABSENCE IS NOT A STATE: an omitted status leaves the ladder exactly as it
 * was before rung 0 existed. Only a status the shared rule KNOWS to be
 * ineligible produces a rung-0 band.
 */
export function consultBandBlockedReason(
  appointmentStatus: string | null | undefined,
): string | null {
  const verdict: ConsultDoorVerdict = consultDoorVerdict(appointmentStatus);
  return verdict.kind === 'blocked' ? verdict.reason : null;
}

/**
 * The truthful NEXT-band label for the consult page's CURRENT state. Pure +
 * exported so the ladder is a PINNED unit (per-rung + the W-23 ratchet), never a
 * render-only behaviour that can silently regress. The caller renders this ONLY
 * while the visit is not finalized (a finalized sheet suppresses the band
 * entirely -- unchanged), so a finalized sheet never needs a rung here.
 */
export function consultNextActionLabel(input: ConsultNextActionInput): string {
  const { sheet, intake, liveRequiredAnswered, appointmentStatus } = input;
  // Rung 0 (Lane 1 item 1 / D01): the visit's stored status admits no
  // consult -- cancelled, no-show, or the person has not arrived. The QA walk
  // found this band coaching "Close the visit when the consult ends ..." over
  // a CANCELLED visit: the last step of a consult that can never run. The
  // band names the STATE instead. It is the same W-23 law this module was
  // built for (NEXT must describe the next EXECUTABLE action) applied one
  // rung earlier: when NOTHING is executable, saying so IS the truthful band.
  const blocked = consultBandBlockedReason(appointmentStatus);
  if (blocked !== null) return blocked;
  // Rung 1: no case sheet materialized yet -> the visit is not started.
  if (sheet === null) return CONSULT_NEXT_START_LABEL;
  // An unresolved read is not evidence of missing answers. Do not offer the
  // completion action or use a stale live-completeness flag before it settles.
  if (intake.kind === 'loading') return CONSULT_NEXT_LOADING_INTAKE_LABEL;
  if (intake.kind === 'error') return CONSULT_NEXT_UNAVAILABLE_INTAKE_LABEL;
  // Rung 2: started, but the required intake is not yet known-ANSWERED.
  if (!intakeCleared(intake, liveRequiredAnswered)) {
    return CONSULT_NEXT_COMPLETE_INTAKE_LABEL;
  }
  // Rung 3: intake cleared, but advised treatment lines await a decision.
  if (rollupPlan(sheet.planItems).advised > 0) {
    return CONSULT_NEXT_RECORD_DECISIONS_LABEL;
  }
  // Rung 4: intake cleared, decisions recorded -> close (copy unchanged).
  return CONSULT_NEXT_CLOSE_LABEL;
}

/**
 * DOCTOR-CONSULT-TRUTH-AND-GATE-CORRECTIVE-V1 (C1 -- no dead doors, LES-016)
 * + DOCTOR-INTAKE-ONE-FLOW-V1 (R3c, founder ruling 2026-08-14). The dock's
 * Go is a DOOR, never a second act: clicking it scrolls to the on-page
 * surface that owns the rung's act. R3c sharpens the intake rungs: when the
 * caller supplies the FIRST UNANSWERED VISIBLE question key (derived by the
 * page from the block's own reason-first order --
 * consultFirstUnansweredKey), the door lands ON THAT FIELD's anchor
 * (`consult-intake-q-<key>`), degrading through the block and the sheet
 * card (the first PRESENT testid wins, so the door can never dangle).
 *
 * #557-corrective coordination (recorded): the corrective landed as #558
 * (doctor HOME only -- zero consult/NextActionBar files), so this map is
 * the ONE owner of the consult Go targeting; the shared NextActionBar
 * primary-as-door ADOPT-LATER marker remains with Lane 2, untouched here.
 */
export function consultNextActionTargets(
  label: string,
  firstUnansweredKey?: string | null,
): readonly string[] {
  const intakeLadder =
    firstUnansweredKey !== undefined && firstUnansweredKey !== null
      ? [
          `consult-intake-q-${firstUnansweredKey}`,
          'consult-intake-block',
          'consult-sheet',
        ]
      : ['consult-intake-block', 'consult-sheet'];
  switch (label) {
    case CONSULT_NEXT_START_LABEL:
      // The consult opens where the paper opens (W-17): the intake card is
      // the first working block; the sheet card is the always-present
      // fallback when the org configures no intake questions.
      return intakeLadder;
    case CONSULT_NEXT_COMPLETE_INTAKE_LABEL:
      return intakeLadder;
    case CONSULT_NEXT_RECORD_DECISIONS_LABEL:
      return ['consult-plan'];
    case CONSULT_NEXT_CLOSE_LABEL:
      return ['consult-close'];
    default:
      // An unknown label gets NO door -- never a control that goes nowhere.
      // A RUNG-0 band lands here BY DESIGN (Lane 1 item 1 / D01): a visit
      // that admits no consult has no on-page act to scroll to, so it gets
      // no Go door rather than one pointing at a surface that cannot help.
      return [];
  }
}
